import { useCallback, useEffect, useState, useRef } from 'react'
import { API } from '../../api'
import { TADUser } from '../../type'
import { handleArrayBufferBackendError } from '../../utility/array-buffer-toast-handling'

type TUseUsers = {
  isLoading: boolean
  users: TADUser[]
  search: string
  page: number
  totalCount: number
  maxPageSize: number
  setExternalUsers: (users: TADUser[]) => void
  setInternalUsers: (users: TADUser[]) => void
  setSearch: (search: string) => void
  setPage: (page: number) => void
  getUsers: () => Promise<void>
  refreshUsers: () => Promise<void>
  handleExportUsersButtonClick: (isExternal: boolean) => void
}

export const useUsers = (externalUsersOnly: boolean, activeOnly = false): TUseUsers => {
  const [isLoading, setIsLoading] = useState(true)
  const [internalUsers, setInternalUsers] = useState<TADUser[]>([])
  const [externalUsers, setExternalUsers] = useState<TADUser[]>([])
  const [search, setSearch] = useState('')
  const [internalPage, setInternalPage] = useState(1)
  const [externalPage, setExternalPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [maxPageSize, setMaxPageSize] = useState(0)

  const abortControllerRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)
  const getUsers = useCallback(
    async (pageToFetch: number) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController()

      setIsLoading(true)
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current

      try {
        const { max_page_size, total_count, results } = externalUsersOnly
          ? await API.ADManagement.user.externalList(search, pageToFetch, abortControllerRef.current.signal, activeOnly)
          : await API.ADManagement.user.internalList(search, pageToFetch, abortControllerRef.current.signal, activeOnly)

        setMaxPageSize(max_page_size as number)
        setTotalCount(total_count as number)
        if (externalUsersOnly) {
          setExternalUsers(results)
          setExternalPage(pageToFetch)
        } else {
          setInternalUsers(results)
          setInternalPage(pageToFetch)
        }
      } catch (error) {
        if ((error as { name?: string }).name === 'AbortError') {
          console.log('Request was aborted')
        } else {
          console.error('Request failed', error)
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      }
    },
    [search, externalUsersOnly, activeOnly],
  )

  useEffect(() => {
    setIsLoading(true)
    getUsers(externalUsersOnly ? externalPage : internalPage)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [getUsers, internalPage, externalPage, externalUsersOnly])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (externalUsersOnly) {
        setExternalPage(newPage)
      } else {
        setInternalPage(newPage)
      }
    },
    [externalUsersOnly],
  )

  const handleExportUsersButtonClick = (isExternal: boolean) => {
    try {
      void API.ADManagement.user.download(isExternal, search, activeOnly)
    } catch (error) {
      handleArrayBufferBackendError(error)
    }
  }

  return {
    isLoading,
    users: externalUsersOnly ? externalUsers : internalUsers,
    search,
    setExternalUsers,
    setInternalUsers,
    setSearch,
    page: externalUsersOnly ? externalPage : internalPage,
    setPage: handlePageChange,
    totalCount,
    maxPageSize,
    getUsers: () => getUsers(externalUsersOnly ? externalPage : internalPage),
    refreshUsers: () => getUsers(externalUsersOnly ? externalPage : internalPage),
    handleExportUsersButtonClick,
  }
}
